import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatDate } from 'config/i18n'
import SendToPatientModal from '../../Events/SendToPatient/SendToPatientModal'
import { sharingEnabled } from '../../Events/SendToPatient/api'

// Mismo gate que SendToPatient.js (Events) — un turno solo es elegible si
// es de Crontu, es un turno real (no bloqueo de agenda) y no está
// cancelado/pendiente de pago. Duplicado a propósito (shape de entrada
// distinto: acá se filtra el array COMPLETO de turnos del paciente).
const isEligible = event =>
  event.source === 'crontu' && (event.type || 'appointment') === 'appointment' && event.status !== 'cancelled' && event.status !== 'pending'

// V2 (card "Envío de archivos a pacientes de Crontu"): botón nuevo en la
// ficha del paciente — antes solo se podía enviar desde el turno. Restricción
// real e ineludible del backend: un envío siempre está atado a un turno
// (crontu exige bookingRef, patient_shares.event_id es NOT NULL) — "enviar
// desde la ficha" en la práctica es "elegir a qué turno elegible se
// adjunta", y de ahí en más es EL MISMO modal que ya existía
// (SendToPatientModal, Events/SendToPatient/ — no se duplica esa lógica).
const SendFilesButton = ({ events, onSent }) => {
  const { t } = useTranslation()
  const [pickerOpen, setPickerOpen] = useState(false)
  const [selected, setSelected] = useState(null)
  const triggerRef = useRef(null)
  const firstOptionRef = useRef(null)

  // Hallazgo real de review: al picker le faltaba foco inicial y restaurarlo
  // al trigger al cerrar (mismo gap que SendToPatientModal, ver ahí).
  useEffect(() => {
    if (!pickerOpen) return
    firstOptionRef.current?.focus()
    const onKeyDown = e => {
      if (e.key === 'Escape') setPickerOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      triggerRef.current?.focus()
    }
  }, [pickerOpen])

  if (!sharingEnabled()) return null

  // Más cercano a "ahora" primero, sea pasado o futuro — mismo criterio que
  // el picker de web (hallazgo real de review: concatenar next+past sin
  // ordenar dejaba una lista sin ningún criterio visible para el usuario).
  const eligible = [...(events?.next || []), ...(events?.past || [])]
    .filter(isEligible)
    .sort((a, b) => Math.abs(a.startsAt - Date.now()) - Math.abs(b.startsAt - Date.now()))

  const open = () => {
    if (eligible.length === 1) {
      setSelected(eligible[0])
    } else {
      setPickerOpen(true)
    }
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="btn btn-link"
        data-testid="send-files-button"
        disabled={eligible.length === 0}
        title={eligible.length === 0 ? t('send_files_no_eligible', 'Este paciente no tiene turnos de Crontu disponibles para enviar archivos') : undefined}
        onClick={open}
      >
        {t('send_files', 'Enviar archivos')}
      </button>

      {pickerOpen && (
        <div
          className="modal show d-flex align-items-center justify-content-center"
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
          aria-labelledby="send-files-picker-title"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', minHeight: '100vh' }}
          onClick={() => setPickerOpen(false)}
        >
          <div
            className="modal-dialog"
            role="document"
            style={{ width: 'min(400px, calc(100vw - 32px))' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="send-files-picker-title">{t('choose_event', 'Elegí el turno')}</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setPickerOpen(false)} />
              </div>
              <div className="modal-body">
                <p className="text-secondary small">
                  {t('choose_event_hint', 'Los archivos se envían junto a un turno — este paciente tiene {{count}} disponibles.', { count: eligible.length })}
                </p>
                <ul className="list-unstyled m-0">
                  {eligible.map((event, index) => (
                    <li key={event.id} className="mb-2">
                      <button
                        ref={index === 0 ? firstOptionRef : undefined}
                        type="button"
                        className="btn btn-outline-secondary w-100 text-start"
                        aria-label={`${t('choose_event_option', 'Turno {{n}} de {{total}}', { n: index + 1, total: eligible.length })}: ${formatDate(event.startsAt, 'EEEE d MMMM yyyy HH:mm')}`}
                        onClick={() => {
                          setPickerOpen(false)
                          setSelected(event)
                        }}
                      >
                        <div>
                          {formatDate(event.startsAt, 'EEEE d MMMM yyyy HH:mm')}
                          {event.startsAt > Date.now() && (
                            <span className="text-primary small"> · {t('choose_event_upcoming', 'Próximo')}</span>
                          )}
                        </div>
                        {(event.service || event.resource) && (
                          <div className="text-secondary small">
                            {event.service?.label}
                            {event.service && event.resource && ' — '}
                            {event.resource?.label}
                          </div>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {selected && (
        // Hallazgo real de review: usar acá el email de la FICHA del
        // paciente (primer email de `emails`, sin noción de "primario") en
        // vez del email real del turno podía mostrar un destinatario
        // distinto al que el backend realmente notifica (resuelve por
        // `bookingRef`, no por este campo) — mismo criterio que el flujo
        // desde el turno (SendToPatient.js), que usa `event.consumer` tal
        // cual.
        <SendToPatientModal event={{ id: selected.id, consumer: selected.consumer }} onClose={() => setSelected(null)} onSent={onSent} />
      )}
    </>
  )
}

export default SendFilesButton
